<script>
import { mapState, mapActions } from "vuex";
import { formatPool, defaultHandle } from "@/utils";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import NavigationWrapper from "@/components/NavigationWrapper";

export default {
  // Included components
  components: {
    NavigationWrapper,
    FontAwesomeIcon,
  },

  data() {
    return {
      // The ID of the worker being edited, or null if creating
      workerId: this.workerId,

      // The name of the worker
      name: "",

      // Password checkbox value
      value: true,

      // Whether worker password is shown or not
      passwordType: "text",

      // The handle for the worker
      handle: "",

      // The serial number for the worker unit
      serialNumber: "",

      // The ID of the device model
      deviceType: null,

      // The destination worker name
      poolWorkerName: "",

      // The destination worker password
      poolWorkerPassword: "",

      // The ID of the pool entry to connect to
      poolId: null,

      // Special notes about worker
      notes: "",
    };
  },

  computed: {
    // Whether the form is being used to edit (vs. create)
    editing() {
      return this.workerId != null;
    },

    // Options for the device selector
    deviceOptions() {
      return this.deviceTypes.map((type) => {
        return { value: type.id, text: type.name };
      });
    },

    // Options for the pool selector
    poolOptions() {
      return this.pools.map((pool) => {
        return {
          value: pool.id,
          text: formatPool(pool, true),
        };
      });
    },

    // Mapped vuex state
    ...mapState(["workers", "deviceTypes", "pools", "user"]),
  },

  watch: {
    // When the name changes, update the handle to be the default
    // handle, unless it has been manually changed
    name(value) {
      // Populate handle field initially if editing
      if (this.editing && this.handle === "") {
        this.handle = defaultHandle(value);
      }

      // Disable further autoupdates if editing
      if (this.handle === "" && !this.editing) {
        this.handle = defaultHandle(value);
      }
    },
  },

  // When the component is created, fetch the worker (if in edit mode)
  // and get the pool and device options
  async created() {
    this.workerId = this.$route.params.workerId;
    this.worker = this.workers[this.workerId];

    if (this.editing) {
      // Get this current worker
      await this.getWorker({ workerId: this.workerId });
      this.worker = this.workers[this.workerId];

      // Set form fields
      this.handle = this.worker.handle;
      this.name = this.worker.name;
      this.deviceType = this.worker.device_id;
      this.password = this.worker.password;

      if (this.worker.pool_configuration) {
        this.poolWorkerName = this.worker.pool_configuration.worker_name;
        this.poolWorkerPassword = this.worker.pool_configuration.password;
        this.poolId = this.worker.pool_configuration.pool_id;
      }

      this.notes = this.worker.notes ? this.worker.notes : "";
      this.serialNumber = this.worker.serial_number
        ? this.worker.serial_number
        : "";
    }

    // Get device types and pools
    await Promise.all([this.getDeviceTypes(), this.getPools()]);
  },

  methods: {
    // On submit, either put or post the form data to the API,
    // depending on whether in edit or create mode
    async submit() {
      if (this.editing) {
        this.put();
      } else {
        this.post();
      }
    },

    // Update the worker via the API
    async put() {
      // Update given the form inputs
      const success = await this.putWorker({
        workerId: this.workerId,
        name: this.name,
        deviceType: this.deviceType,
        password: "",
        poolWorkerName: this.poolWorkerName,
        poolWorkerPassword: this.poolWorkerPassword,
        poolId: this.poolId,
        handle: this.handle,
        serial_number: this.serialNumber,
        notes: this.notes,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/workers/" + this.workerId);
      }
    },

    // Post the new worker via the API
    async post() {
      // Update given the form inputs
      const success = await this.postWorker({
        name: this.name,
        deviceType: this.deviceType,
        password: "",
        poolWorkerName: this.poolWorkerName,
        poolWorkerPassword: this.poolWorkerPassword,
        poolId: this.poolId,
        handle: this.handle,
        serial_number: this.serialNumber,
        notes: this.notes,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/workers");
      }
    },

    togglePassword() {
      if (this.passwordType === "password") {
        this.passwordType = "text";
      } else {
        this.passwordType = "password";
      }
    },

    // Mapped vuex actions
    ...mapActions([
      "getWorker",
      "putWorker",
      "postWorker",
      "getDeviceTypes",
      "getPools",
    ]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/workers">Workers</b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" :to="`/workers/${workerId}`">
        {{ worker.name }}
      </b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" active>Edit</b-breadcrumb-item>
      <b-breadcrumb-item v-else active>Add Worker</b-breadcrumb-item>
    </template>
    <h1 class="mb-3">
      {{ editing ? "Edit Worker Configuration" : "Add Worker" }}
    </h1>
    <b-row>
      <b-col>
        <b-card class="mb-3">
          <b-form @submit.stop.prevent="submit">
            <b-row>
              <b-col>
                <b-form-group id="name-group" label="Name" label-for="name">
                  <b-form-input
                    required
                    id="name"
                    v-model="name"
                    placeholder="Name"
                  ></b-form-input>
                  <small class="form-text text-muted">
                    Human-readable name for the worker.
                  </small>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  id="handle-group"
                  label="Handle"
                  label-for="handle"
                >
                  <input
                    id="handle"
                    v-model="handle"
                    placeholder="Handle"
                    pattern="[a-z,0-9]([a-z,0-9]|(-[a-z,0-9]))*"
                    minlength="1"
                    maxlength="64"
                    autocomplete="off"
                    class="form-control"
                  />
                  <small class="form-text text-muted">
                    The worker's handle is used in miner pool settings, like
                    <b>my-team.the-handle</b>
                    .
                  </small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group
              id="serial-number-group"
              label="Serial Number"
              label-for="serialNumber"
            >
              <b-form-input
                v-model="serialNumber"
                id="serialNumber"
                placeholder="Serial number (optional)"
              />
              <small class="form-text text-muted">
                The serial number is the worker unit's unique identifier.
              </small>
            </b-form-group>
            <b-form-group
              id="device-type-group"
              label="Device Type"
              label-for="deviceType"
            >
              <b-form-select
                required
                v-model="deviceType"
                id="deviceType"
                :options="deviceOptions"
              >
                <template #first>
                  <option :value="null" disabled>Device</option>
                </template>
              </b-form-select>
              <small class="form-text text-muted">
                Type of device connecting to this proxy.
              </small>
            </b-form-group>
            <b-form-group
              id="destination-worker-pool-group"
              label="Pool"
              label-for="destinationWorkerPool"
            >
              <b-form-select
                required
                v-model="poolId"
                id="destinationWorkerPool"
                :options="poolOptions"
              >
                <template #first>
                  <option :value="null" disabled>Pool</option>
                </template>
              </b-form-select>
              <small class="form-text text-muted">
                Pool that the worker will connect to.
              </small>
            </b-form-group>
            <b-row>
              <b-col>
                <b-form-group
                  id="destination-worker-name-group"
                  label="Pool Worker Name"
                  label-for="destinationWorkerName"
                >
                  <b-form-input
                    required
                    id="destinationWorkerName"
                    v-model="poolWorkerName"
                    placeholder="Pool Worker Name"
                    autocomplete="off"
                  ></b-form-input>
                  <small class="form-text text-muted">
                    Name that will appear to the pool.
                  </small>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  id="destination-worker-password-group"
                  label="Pool Worker Password"
                  label-for="destinationWorkerPassword"
                >
                  <b-input-group>
                    <b-form-input
                      id="destinationWorkerPassword"
                      :type="this.passwordType"
                      v-model="poolWorkerPassword"
                      placeholder="Pool Worker Password"
                      autocomplete="new-password"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button
                        id="toggle-btn"
                        v-on:click="togglePassword"
                        variant="outline-secondary"
                      >
                        <FontAwesomeIcon icon="eye-slash" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="form-text text-muted">
                    Password used by this proxy to authenticate to the pool.
                  </small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group
              id="notes-group"
              label="Notes (Optional)"
              label-for="notes"
            >
              <b-form-textarea
                id="notes"
                v-model="notes"
                placeholder="Notes"
                class="form-control"
                rows="4"
                no-resize
              ></b-form-textarea>
              <small class="form-text text-muted">
                Any important information to remember about workers.
              </small>
            </b-form-group>
            <b-button type="submit" variant="primary">
              {{ editing ? "Update" : "Add" }} Worker
            </b-button>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>

<style>
#toggle-btn {
  padding: 7px 12px;
  margin-top: -1px;
}
</style>
